/**
 * FloorPlanCertification flow DTO + completeness predicate (PF v2 native module).
 *
 * The module's durable state IS this DTO, stored at
 * `productConfigs["${cartItemId}|floor_plan_certification"]` (MODULE-CONTRACT
 * Pattern A). It is a plain-JSON projection of the legacy
 * `FloorPlanCertificationRequestDTO` (`models/purchaseFlow/floorPlanCertificationDTO`):
 * the model types `floors` as `ImmutableList<…>`, but the *persisted* shape is a
 * plain array — Immutable's `List.toJSON()` runs at `JSON.stringify` on save, so
 * what actually lands in `configs[key]` (and hydrates back) is a plain array of
 * `{ name, floorNumber }`. The native module holds plain arrays end-to-end → no
 * Immutable.js in the store, and wire-compat is automatic (drafts serialized under
 * the old adapter hydrate here with zero migration; `PF_VERSION` stays frozen at 3).
 *
 * ZERO React / store imports — `modules/completeness.ts` (which `store/selectors.ts`
 * imports) pulls `floorPlanCertIsComplete` from here, so this file must stay pure.
 *
 * Enum home (PM DECISION — DF-7): `FloorPlanCertificationPropertyTypes` and
 * `AreaCalculationTypes` live under `components/pages/PurchaseFlow/*` — a forbidden
 * import for the module (DoD grep gate). They are re-declared LOCALLY here,
 * byte-identical to the legacy source
 * (`PurchaseFlow/steps/FloorPlanCertification/_main/FloorPlanCertification.constants.ts`).
 * Relocating the legacy enums into `models/` (the cleaner fix) is deferred to the Z
 * cleanup wave. The local enums are nominal-distinct TS types but runtime-compatible
 * (identical string values), so the persisted/wire shape is preserved verbatim.
 */

/**
 * Property types offered by the floor-plan-certification flow. Re-declared
 * byte-identical from the legacy `FloorPlanCertification.constants.ts`.
 */
export enum FloorPlanCertificationPropertyTypes {
  FARMHOUSE = 'FARMHOUSE',
  BUNGALOW = 'BUNGALOW',
  OFFICE = 'OFFICE',
  SEMI_DETACHED_HOUSE = 'SEMI_DETACHED_HOUSE',
  CONDOMINIUM = 'CONDOMINIUM',
  DETACHED_HOUSE = 'DETACHED_HOUSE',
  RETAIL = 'RETAIL',
  GARAGE_PARKING_SPACE = 'GARAGE_PARKING_SPACE',
  GASTRONOMY = 'GASTRONOMY',
  PLOT_OF_LAND = 'PLOT_OF_LAND',
  HALL_WAREHOUSE_PRODUCTION = 'HALL_WAREHOUSE_PRODUCTION',
  HOUSE = 'HOUSE',
  STORE = 'STORE',
  APARTMENT_BUILDING_FLOOR = 'APARTMENT_BUILDING_FLOOR',
  PRACTICE = 'PRACTICE',
  END_TERRACED_HOUSE = 'END_TERRACED_HOUSE',
  TERRACED_HOUSE = 'TERRACED_HOUSE',
  RESIDENTIAL_AND_COMMERCIAL_BUILDING = 'RESIDENTIAL_AND_COMMERCIAL_BUILDING',
  RESIDENTIAL_BUILDING = 'RESIDENTIAL_BUILDING',
  APARTMENT = 'APARTMENT',
  OTHER = 'OTHER',
}

/**
 * Regulated-living-area calculation rules (WoFIV). Re-declared byte-identical from
 * the legacy `FloorPlanCertification.constants.ts`.
 */
export enum AreaCalculationTypes {
  CALCULATE_WITH_25 = 'CALCULATE_WITH_25',
  CALCULATE_WITH_50 = 'CALCULATE_WITH_50',
}

/**
 * One floor entry as it is persisted on the wire (plain object). Mirrors the model
 * `FloorPlanCertificationFloorDTO` (`models/purchaseFlow/floorPlanCertificationDTO`);
 * re-declared locally so the module has no dependency on that model file (which
 * carries the forbidden `PurchaseFlow/*` enum import).
 */
export type FloorPlanCertificationFloorDTO = {
  /** The name of the floor. */
  name: string
  /** Floor order number from 1 to x. */
  floorNumber: number
}

/**
 * Plain-JSON projection of `FloorPlanCertificationRequestDTO` — the shape actually
 * persisted in drafts (`floors` is a PLAIN array, never an ImmutableList).
 */
export type FloorPlanCertificationDraftDTO = {
  /** The property type being certified. */
  propertyType: FloorPlanCertificationPropertyTypes
  /** Number of floors for certification. */
  numberOfFloors: number
  /** Per-floor metadata (plain array; `floors.length === numberOfFloors`). */
  floors: FloorPlanCertificationFloorDTO[]
  /** The area calculation rule (WoFIV). */
  regulatedLivingAreaCalculation: AreaCalculationTypes | null
  /** Free-text comment. */
  comment: string
}

/**
 * Read-time fallback for controlled inputs. NEVER written to the store on mount
 * (read-only-mount invariant, MODULE-CONTRACT §3). The ops hook field-merges this
 * over the (possibly partial / absent) persisted DTO so the UI always sees every
 * top-level field defined.
 *
 * Matches the legacy `defaultFloorPlanCertificationInformation`
 * (`FloorPlanCertificationStep.context.ts`) once the FE-only sentinel floor is
 * dropped: APARTMENT / 1 floor / one empty-named real floor / CALCULATE_WITH_25 /
 * empty comment.
 */
export const FLOOR_PLAN_CERT_DEFAULT: FloorPlanCertificationDraftDTO = {
  propertyType: FloorPlanCertificationPropertyTypes.APARTMENT,
  numberOfFloors: 1,
  floors: [{ name: '', floorNumber: 1 }],
  regulatedLivingAreaCalculation: AreaCalculationTypes.CALCULATE_WITH_25,
  comment: '',
}

/**
 * Pure completeness predicate — the ONLY source of truth for FPC completeness.
 *
 * Mirrors the legacy `isFloorPlanCertificationStepValid`
 * (`PurchaseFlowUtilities.context`): at least one floor, and every floor has a
 * non-empty name. The persisted DTO carries NO sentinel (legacy serialize did
 * `floors.shift()`), so we check ALL floors — `floors[i].floorNumber === i + 1`
 * and `floors.length === numberOfFloors`.
 *
 * The `length === numberOfFloors` guard is added robustness for the partial-DTO
 * case that legacy full data cannot hit (RISK-1 sparse resume); legacy full data
 * always satisfies it, so parity holds.
 *
 * NOTE — unlike measurement (always `true`), this is a REAL gate: the default DTO
 * has an empty-named floor, so an untouched form is INCOMPLETE and Save stays
 * disabled until the user names every floor (legacy parity).
 */
export const floorPlanCertIsComplete = (dto: FloorPlanCertificationDraftDTO | undefined): boolean => {
  if (!dto || !(dto.numberOfFloors > 0)) return false
  const floors = dto.floors ?? []
  if (floors.length !== dto.numberOfFloors) return false // safety vs partial DTOs
  return floors.every((floor) => !!floor?.name) // legacy uses truthiness (no trim)
}
